import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Image,
  Video,
  Code,
  Link,
  LayoutDashboard,
  MoreVertical,
  Trash2,
  Edit,
  Clock,
  Eye,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ContentItem {
  id: string;
  name: string;
  description: string | null;
  content_type: 'image' | 'video' | 'html' | 'url' | 'dashboard';
  storage_path: string | null;
  external_url: string | null;
  html_content: string | null;
  dashboard_type: string | null;
  duration_seconds: number;
  thumbnail_url: string | null;
  is_active: boolean;
  created_at: string;
}

interface ContentCardProps {
  content: ContentItem;
  onDelete: () => void;
  onRefresh: () => void;
}

export function ContentCard({ content, onDelete, onRefresh }: ContentCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (content.thumbnail_url) {
      setThumbnailUrl(content.thumbnail_url);
    } else if (content.storage_path && (content.content_type === 'image' || content.content_type === 'video')) {
      // Get signed URL for preview
      getSignedUrl();
    }
  }, [content]);

  const getSignedUrl = async () => {
    if (!content.storage_path) return;

    try {
      const { data } = await supabase.storage
        .from('signage-content')
        .createSignedUrl(content.storage_path, 3600);

      if (data?.signedUrl) {
        setThumbnailUrl(data.signedUrl);
      }
    } catch (err) {
      console.error('Failed to get thumbnail:', err);
    }
  };

  const getTypeIcon = () => {
    switch (content.content_type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'html':
        return <Code className="w-4 h-4" />;
      case 'url':
        return <Link className="w-4 h-4" />;
      case 'dashboard':
        return <LayoutDashboard className="w-4 h-4" />;
      default:
        return <Image className="w-4 h-4" />;
    }
  };

  const getTypeLabel = () => {
    switch (content.content_type) {
      case 'image':
        return 'Image';
      case 'video':
        return 'Video';
      case 'html':
        return 'HTML';
      case 'url':
        return 'URL';
      case 'dashboard':
        return content.dashboard_type || 'Dashboard';
      default:
        return 'Content';
    }
  };

  const getPlaceholderBg = () => {
    switch (content.content_type) {
      case 'image':
        return 'bg-blue-500/10';
      case 'video':
        return 'bg-purple-500/10';
      case 'html':
        return 'bg-orange-500/10';
      case 'url':
        return 'bg-green-500/10';
      case 'dashboard':
        return 'bg-pink-500/10';
      default:
        return 'bg-muted';
    }
  };

  return (
    <>
      <Card className="overflow-hidden group">
        {/* Thumbnail */}
        <div className="aspect-video relative bg-muted">
          {thumbnailUrl ? (
            content.content_type === 'video' ? (
              <video
                src={thumbnailUrl}
                className="w-full h-full object-cover"
                muted
                playsInline
              />
            ) : (
              <img
                src={thumbnailUrl}
                alt={content.name}
                className="w-full h-full object-cover"
              />
            )
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${getPlaceholderBg()}`}>
              <div className="text-muted-foreground">
                {React.cloneElement(getTypeIcon() as React.ReactElement, { className: 'w-8 h-8' })}
              </div>
            </div>
          )}

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" disabled>
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>
          </div>

          {/* Type badge */}
          <Badge
            variant="secondary"
            className="absolute top-2 left-2 text-xs"
          >
            {getTypeIcon()}
            <span className="ml-1">{getTypeLabel()}</span>
          </Badge>

          {/* Duration badge */}
          <Badge
            variant="secondary"
            className="absolute bottom-2 right-2 text-xs"
          >
            <Clock className="w-3 h-3 mr-1" />
            {content.duration_seconds}s
          </Badge>
        </div>

        {/* Content info */}
        <CardContent className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-sm truncate">{content.name}</h3>
              {content.description && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {content.description}
                </p>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem disabled>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Content</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{content.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
